/**
 * Metrics Helper Utility
 * Simple utility to add processing time to metrics payloads
 */

/**
 * Start timing for an action
 * @returns {number} Start time in milliseconds
 */
export function startTiming() {
  return performance.now();
}

/**
 * Calculate processing time and enhance metrics payload
 * @param {number} startTime - Start time from performance.now()
 * @param {Object} metricsPayload - Original metrics payload
 * @param {string} status - Optional status override ('Completed', 'Error', etc.)
 * @param {string} errorMessage - Optional error message
 * @returns {Object} Enhanced metrics payload with processingTime
 */
export function addProcessingTime(startTime, metricsPayload, status = null, errorMessage = null) {
  // ALWAYS initialize to 0 - never let it be null or undefined
  let processingTime = 0;
  
  try {
    const endTime = performance.now();
    // More robust validation and calculation
    if (startTime !== null && startTime !== undefined && 
        typeof startTime === 'number' && !isNaN(startTime) && 
        endTime !== null && endTime !== undefined && 
        typeof endTime === 'number' && !isNaN(endTime)) {
      
      const rawProcessingTime = endTime - startTime;
      // Ensure we have a positive number
      const calculatedTime = Math.max(0, Math.round(rawProcessingTime));
      
      // Double check it's a valid number before assigning
      if (typeof calculatedTime === 'number' && !isNaN(calculatedTime)) {
        processingTime = calculatedTime;
      } 
    } 
  } catch (error) {
    processingTime = 0; // Ensure it's still 0 even if there's an error
  }
  
  // Ensure processingTime is NEVER null or undefined
  if (processingTime === null || processingTime === undefined || isNaN(processingTime)) {
    console.warn('ProcessingTime was null/undefined/NaN, forcing to 0');
    processingTime = 0;
  }
  const result = {
    ...metricsPayload,
    processingTime: Number(processingTime), // Force it to be a number
    timestamp: new Date().toISOString(),
    ...(status && { status }),
    ...(errorMessage && { errorMessage })
  };
  return result;
}

/**
 * Add processing time for error scenarios
 * @param {number} startTime - Start time from performance.now()
 * @param {Object} metricsPayload - Original metrics payload
 * @param {Error} error - Error object
 * @returns {Object} Enhanced metrics payload with processingTime and error info
 */
export function addProcessingTimeForError(startTime, metricsPayload, error) {
  return addProcessingTime(startTime, metricsPayload, 'Error', error.message);
}
